import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft, ArrowRight, Check, Briefcase, FileText, Handshake, GraduationCap, Store } from 'lucide-react';
import { DocumentUpload } from '@/components/opportunities/DocumentUpload';

const TYPES = [
  { value: 'job', icon: Briefcase, label: 'Job' },
  { value: 'tender', icon: FileText, label: 'Tender / RFP' },
  { value: 'jv', icon: Handshake, label: 'Joint Venture' },
  { value: 'training', icon: GraduationCap, label: 'Training Program' },
  { value: 'service_offer', icon: Store, label: 'Service Offer' },
] as const;

const SECTORS = ['mining', 'processing', 'infrastructure', 'industrial', 'services', 'other'] as const;
const ELIGIBILITY = ['local', 'diaspora_welcome', 'international'] as const;
const SPA_PILLARS = ['critical_minerals', 'industrialization', 'infrastructure', 'governance'] as const;

export default function CreateOpportunity() {
  const { t } = useTranslation();
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyType, setCompanyType] = useState<string | null>(null);

  // Form fields
  const [type, setType] = useState<'job' | 'tender' | 'jv' | 'training' | 'service_offer'>('job');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sector, setSector] = useState<typeof SECTORS[number]>('mining');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [remote, setRemote] = useState(false);
  const [eligibility, setEligibility] = useState<typeof ELIGIBILITY[number]>('local');
  const [spaLinked, setSpaLinked] = useState(false);
  const [spaPillar, setSpaPillar] = useState<typeof SPA_PILLARS[number] | ''>('');
  const [deadline, setDeadline] = useState('');
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && (!user || role !== 'company')) {
      navigate('/auth');
      return;
    }

    if (user) {
      fetchCompany();
    }
  }, [user, role, authLoading, navigate]);

  const fetchCompany = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from('companies')
        .select('id, company_type')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setCompanyId(data.id);
        setCompanyType(data.company_type);
        // Auto-set type based on company_type
        if (data.company_type === 'congolese_sme') {
          setType('service_offer');
        } else if (type === 'service_offer') {
          setType('job');
        }
      } else {
        toast({ title: t('company.dashboard.completeProfileFirst'), variant: 'destructive' });
        navigate('/profile/company/setup');
      }
    } catch (error) {
      navigate('/profile/company/setup');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (asDraft: boolean) => {
    if (!companyId) return;

    setSaving(true);

    try {
      const { error } = await supabase.from('opportunities').insert({
        company_id: companyId,
        type,
        title,
        description,
        sector,
        country,
        city: city || null,
        remote,
        eligibility,
        spa_linked: spaLinked,
        spa_pillar: spaLinked && spaPillar ? spaPillar : null,
        deadline: deadline || null,
        document_url: documentUrl,
        status: asDraft ? 'draft' : 'open',
        published_at: asDraft ? null : new Date().toISOString(),
      });

      if (error) throw error;

      toast({ title: asDraft ? 'Saved as draft' : 'Opportunity published!' });
      navigate('/dashboard/company');
    } catch (error) {
      console.error('Error creating opportunity:', error);
      toast({ title: 'Error creating opportunity', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="container flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-2xl py-8">
        <h1 className="mb-8 text-3xl font-bold">{t('opportunities.create.title')}</h1>

        {/* Progress Steps */}
        <div className="mb-8 flex items-center justify-between">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full border-2 font-medium ${
                  s < step
                    ? 'border-primary bg-primary text-primary-foreground'
                    : s === step
                    ? 'border-primary text-primary'
                    : 'border-muted text-muted-foreground'
                }`}
              >
                {s < step ? <Check className="h-5 w-5" /> : s}
              </div>
              {s < 4 && (
                <div
                  className={`mx-2 h-0.5 w-12 sm:w-20 ${
                    s < step ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {step === 1 && t('opportunities.create.step1')}
              {step === 2 && t('opportunities.create.step2')}
              {step === 3 && t('opportunities.create.step3')}
              {step === 4 && t('opportunities.create.step4')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Step 1: Type Selection */}
            {step === 1 && (
              <div className="space-y-4">
                <p className="text-muted-foreground">{t('opportunities.create.selectType')}</p>
                <RadioGroup
                  value={type}
                  onValueChange={(v) => setType(v as typeof type)}
                  className="grid gap-4 sm:grid-cols-2"
                >
                  {TYPES.filter((t) => {
                    if (companyType === 'congolese_sme') return t.value === 'service_offer';
                    return t.value !== 'service_offer';
                  }).map((t) => (
                    <Label
                      key={t.value}
                      className={`flex cursor-pointer items-center gap-4 rounded-lg border-2 p-4 transition-colors ${
                        type === t.value ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}
                    >
                      <RadioGroupItem value={t.value} className="sr-only" />
                      <t.icon className="h-6 w-6" />
                      <span className="font-medium">{t.label}</span>
                    </Label>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Step 2: Basic Info */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="title">{t('opportunities.create.titleLabel')} *</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">{t('opportunities.create.descriptionLabel')} *</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={6}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('opportunities.create.sectorLabel')} *</Label>
                  <Select value={sector} onValueChange={(v) => setSector(v as typeof sector)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTORS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {t(`sectors.${s}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t('opportunities.create.countryLabel')} *</Label>
                    <Select value={country} onValueChange={setCountry}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CD">DR Congo</SelectItem>
                        <SelectItem value="US">United States</SelectItem>
                        <SelectItem value="ZA">South Africa</SelectItem>
                        <SelectItem value="GB">United Kingdom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">{t('opportunities.create.cityLabel')}</Label>
                    <Input
                      id="city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Checkbox
                    id="remote"
                    checked={remote}
                    onCheckedChange={(checked) => setRemote(checked as boolean)}
                  />
                  <Label htmlFor="remote" className="cursor-pointer">
                    {t('opportunities.create.remoteLabel')}
                  </Label>
                </div>
              </div>
            )}

            {/* Step 3: Eligibility & SPA */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="space-y-3">
                  <Label>{t('opportunities.create.eligibilityLabel')} *</Label>
                  <Select value={eligibility} onValueChange={(v) => setEligibility(v as typeof eligibility)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ELIGIBILITY.map((e) => (
                        <SelectItem key={e} value={e}>
                          {t(`eligibility.${e}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-4 rounded-lg border p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="spaLinked"
                      checked={spaLinked}
                      onCheckedChange={(checked) => setSpaLinked(checked as boolean)}
                    />
                    <Label htmlFor="spaLinked" className="cursor-pointer leading-tight">
                      {t('opportunities.create.spaLinkedLabel')}
                    </Label>
                  </div>

                  {spaLinked && (
                    <div className="space-y-2">
                      <Label>{t('opportunities.create.spaPillarLabel')}</Label>
                      <Select value={spaPillar} onValueChange={(v) => setSpaPillar(v as typeof spaPillar)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select pillar..." />
                        </SelectTrigger>
                        <SelectContent>
                          {SPA_PILLARS.map((p) => (
                            <SelectItem key={p} value={p}>
                              {t(`spaPillars.${p}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deadline">{t('opportunities.create.deadlineLabel')}</Label>
                  <Input
                    id="deadline"
                    type="date"
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                  />
                </div>

                {/* Document upload for Tenders and JVs */}
                {(type === 'tender' || type === 'jv') && (
                  <DocumentUpload
                    label={t('opportunities.create.documentLabel', 'Terms of Reference / RFP Document')}
                    description={t('opportunities.create.documentDescription', 'Upload the full TOR, RFP, or specifications document for bidders to review.')}
                    folder="opportunity-documents"
                    value={documentUrl}
                    onChange={setDocumentUrl}
                  />
                )}
              </div>
            )}

            {/* Step 4: Review */}
            {step === 4 && (
              <div className="space-y-6">
                <p className="text-muted-foreground">{t('opportunities.create.review')}</p>

                <div className="space-y-4 rounded-lg border p-4">
                  <div>
                    <span className="text-sm text-muted-foreground">Type:</span>
                    <p className="font-medium">{t(`opportunities.types.${type}`)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Title:</span>
                    <p className="font-medium">{title}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Sector:</span>
                    <p className="font-medium">{t(`sectors.${sector}`)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Location:</span>
                    <p className="font-medium">
                      {city ? `${city}, ` : ''}{country} {remote && '(Remote)'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Eligibility:</span>
                    <p className="font-medium">{t(`eligibility.${eligibility}`)}</p>
                  </div>
                  {spaLinked && (
                    <div>
                      <span className="text-sm text-muted-foreground">SPA Pillar:</span>
                      <p className="font-medium">{spaPillar && t(`spaPillars.${spaPillar}`)}</p>
                    </div>
                  )}
                  {documentUrl && (
                    <div>
                      <span className="text-sm text-muted-foreground">Document:</span>
                      <p className="font-medium">
                        <a href={documentUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          View uploaded document
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="mt-8 flex justify-between">
              {step > 1 ? (
                <Button variant="outline" onClick={() => setStep(step - 1)}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('common.previous')}
                </Button>
              ) : (
                <div />
              )}

              {step < 4 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={
                    (step === 2 && (!title || !description || !country)) ||
                    (step === 3 && !eligibility)
                  }
                >
                  {t('common.next')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => handleSubmit(true)}
                    disabled={saving}
                  >
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('opportunities.create.saveAsDraft')}
                  </Button>
                  <Button
                    onClick={() => handleSubmit(false)}
                    disabled={saving}
                  >
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('opportunities.create.publish')}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}